import { ArrowLeft, Bell, BellOff, Copy, ExternalLink, FileText, MoreHorizontal, Paperclip, Pencil, SendHorizontal, X } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { toApiError, type ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { MenuItem, Popover } from '@/components/ui/Popover'
import { Spinner } from '@/components/ui/Spinner'
import { ErrorState } from '@/components/ui/StatePanel'
import { statusLabel } from '@/features/sales/orders/model'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { chatService, type Chat, type ChatAttachment, type ChatMessage } from '@/services/core/chat.service'
import { filesService } from '@/services/core/files.service'
import { useStores } from '@/stores'
import type { UploadedFile } from '@/types/api'
import { ChatAvatar } from './ChatList'
import {
  MAX_BODY,
  MAX_FILES,
  MAX_FILE_SIZE,
  canEdit,
  chatTitle,
  dayKey,
  formatClock,
  formatDayLabel,
  formatSize,
  isDeleted,
  isImage,
  newClientMessageId,
  roleLabel,
  systemText,
} from './model'

const PAGE = 50
const POLL_MS = 3_000
/** Messages of one sender closer than this share one name line. */
const RUN_MS = 5 * 60_000
const NEAR_BOTTOM = 96

type Draft = { id: string; name: string; size: number; content_type: string; progress: number; preview?: string; uploaded?: UploadedFile; failed?: boolean }

function merge(current: ChatMessage[], incoming: ChatMessage[]) {
  if (incoming.length === 0) return current
  const byId = new Map(current.map((message) => [message.id, message]))
  for (const message of incoming) byId.set(message.id, message)
  return [...byId.values()].sort((left, right) => left.id - right.id)
}

const textSize = (px: number) => `text-[length:calc(${px}px*var(--text-scale))]`

function Attachments({ files, mine }: { files: ChatAttachment[]; mine: boolean }) {
  const images = files.filter(isImage)
  const others = files.filter((file) => !isImage(file))
  return (
    <div className="flex flex-col gap-1.5">
      {images.length > 0 && (
        <div className={cn('grid gap-1', images.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
          {images.map((file) => (
            <a key={file.id} href={file.url} target="_blank" rel="noreferrer" title={file.name} className="block overflow-hidden rounded-[calc(8px*var(--radius-scale))] bg-hover">
              <img src={file.url} alt={file.name} loading="lazy" className={cn('w-full object-cover', images.length > 1 ? 'h-28' : 'max-h-56 min-h-20')} />
            </a>
          ))}
        </div>
      )}
      {others.map((file) => (
        <a
          key={file.id}
          href={file.url}
          target="_blank"
          rel="noreferrer"
          download={file.name}
          className={cn(
            'flex min-w-0 items-center gap-2 rounded-[calc(8px*var(--radius-scale))] px-2 py-1.5 transition-colors',
            mine ? 'bg-white/15 hover:bg-white/25' : 'bg-hover hover:bg-press',
          )}
        >
          <FileText className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <span className={cn('num shrink-0', textSize(11), mine ? 'text-accent-fg/75' : 'text-muted')}>{formatSize(file.size)}</span>
        </a>
      ))}
    </div>
  )
}

function MessageMenu({ message, onEdit, open }: { message: ChatMessage; onEdit: () => void; open: (fn: () => void) => void }) {
  const t = useT()
  return (
    <Popover
      align={message.mine ? 'end' : 'start'}
      trigger={({ toggle, ...aria }) => {
        open(() => toggle(true))
        return (
          <button
            type="button"
            {...aria}
            onClick={() => toggle()}
            aria-label={t('chat.messageMenu')}
            className="flex size-6 items-center justify-center rounded-full text-muted opacity-0 transition-opacity hover:bg-hover hover:text-fg focus-visible:opacity-100 group-hover:opacity-100 aria-expanded:opacity-100"
          >
            <MoreHorizontal className="size-4" />
          </button>
        )
      }}
    >
      {(close) => (
        <>
          {message.body && (
            <MenuItem
              icon={<Copy className="size-4" />}
              onSelect={() => {
                close()
                void navigator.clipboard?.writeText(message.body)
              }}
            >
              {t('chat.actions.copy')}
            </MenuItem>
          )}
          {canEdit(message) && (
            <MenuItem
              icon={<Pencil className="size-4" />}
              onSelect={() => {
                close()
                onEdit()
              }}
            >
              {t('chat.actions.edit')}
            </MenuItem>
          )}
        </>
      )}
    </Popover>
  )
}

function MessageRow({
  message,
  chat,
  showName,
  onEdit,
}: {
  message: ChatMessage
  chat: Chat
  showName: boolean
  onEdit: (message: ChatMessage) => void
}) {
  const t = useT()
  const openMenu = useRef<(() => void) | null>(null)

  if (message.kind === 'system') {
    return (
      <div className="flex justify-center px-4 py-1">
        <span className={cn('max-w-[90%] rounded-full bg-hover px-3 py-1 text-center text-muted', textSize(12))}>
          {systemText(message, t)} · <span className="num">{formatClock(message.created_at)}</span>
        </span>
      </div>
    )
  }

  const deleted = isDeleted(message)
  const mine = message.mine
  // Copy for any text, edit for an own text message within the hour; messages are never deleted.
  const hasMenu = !deleted && (Boolean(message.body) || canEdit(message))
  const role = roleLabel(message.sender_role_code, t)
  return (
    <div className={cn('group flex items-end gap-1 px-3', mine ? 'flex-row-reverse' : 'flex-row', showName ? 'pt-2' : 'pt-0.5')}>
      <div
        onContextMenu={(event) => {
          if (!hasMenu || !openMenu.current) return
          event.preventDefault()
          openMenu.current()
        }}
        className={cn(
          'min-w-0 max-w-[min(78%,520px)] rounded-[calc(14px*var(--radius-scale))] px-3 py-1.5',
          textSize(13.5),
          deleted ? 'border border-dashed border-line text-muted italic' : mine ? 'bg-accent text-accent-fg' : 'bg-glass-solid text-fg shadow-[0_1px_2px_rgba(0,0,0,0.06)]',
          mine ? 'rounded-br-[calc(5px*var(--radius-scale))]' : 'rounded-bl-[calc(5px*var(--radius-scale))]',
        )}
      >
        {showName && !mine && chat.kind !== 'direct' && (
          <div className={cn('mb-0.5 flex items-baseline gap-1.5 truncate', textSize(12))}>
            <span className="truncate font-semibold text-accent">{message.sender_name || '—'}</span>
            {role && <span className="truncate text-muted">{role}</span>}
          </div>
        )}
        {deleted ? (
          <span>{t('chat.deleted')}</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {message.attachments.length > 0 && <Attachments files={message.attachments} mine={mine} />}
            {message.body && <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.body}</p>}
          </div>
        )}
        <div className={cn('mt-0.5 flex justify-end gap-1.5', textSize(10.5), deleted ? 'text-subtle' : mine ? 'text-accent-fg/75' : 'text-subtle')}>
          {message.edited_at && !deleted && <span>{t('chat.edited')}</span>}
          <span className="num">{formatClock(message.created_at)}</span>
        </div>
      </div>
      {hasMenu && (
        <MessageMenu
          message={message}
          open={(fn) => {
            openMenu.current = fn
          }}
          onEdit={() => onEdit(message)}
        />
      )}
    </div>
  )
}

/**
 * One conversation: the messages oldest to newest (older pages load on
 * scrolling up), new ones polled every 3 s while it is open, the read mark
 * moved to the last message, and the composer (Enter sends, Shift+Enter is
 * a new line; files go through POST /file-upload first). The drawer, the
 * «Чат» page and the order's «Чат» all show this component; mount it with
 * `key={chatId}`.
 */
export const Conversation = observer(function Conversation({
  chatId,
  initialChat,
  onBack,
  orderLink = true,
  headerActions,
  onChatChange,
}: {
  chatId: string
  initialChat?: Chat | null
  onBack?: () => void
  /** «Открыть заказ» in an order chat's header; off inside the order itself. */
  orderLink?: boolean
  headerActions?: (chat: Chat) => ReactNode
  onChatChange?: (chat: Chat) => void
}) {
  const t = useT()
  const navigate = useNavigate()
  const { chat: chatStore, toasts, session } = useStores()
  const [chat, setChat] = useState<Chat | null>(initialChat ?? null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<ApiError | null>(null)
  const [older, setOlder] = useState<'idle' | 'loading' | 'error'>('idle')
  const [draft, setDraft] = useState('')
  const [files, setFiles] = useState<Draft[]>([])
  const [sending, setSending] = useState(false)
  const [editing, setEditing] = useState<ChatMessage | null>(null)
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible')
  const [reloadKey, setReloadKey] = useState(0)

  const scroller = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const atBottom = useRef(true)
  const pendingScroll = useRef<{ kind: 'bottom' } | { kind: 'keep'; height: number; top: number } | null>(null)
  const lastId = useRef(0)
  const readSent = useRef(0)
  const clientId = useRef(newClientMessageId())
  const savedDraft = useRef('')
  const onChatChangeRef = useRef(onChatChange)
  useLayoutEffect(() => {
    onChatChangeRef.current = onChatChange
  })

  const applyChat = useCallback((next: Chat) => {
    setChat(next)
    onChatChangeRef.current?.(next)
  }, [])

  const reloadChat = useCallback(async () => {
    try {
      applyChat(await chatService.get(chatId))
    } catch {
      // The header keeps what it shows.
    }
  }, [chatId, applyChat])

  // First page and the chat header.
  useEffect(() => {
    const controller = new AbortController()
    const run = async () => {
      try {
        const [header, page] = await Promise.all([chatService.get(chatId, controller.signal), chatService.messages(chatId, { limit: PAGE }, controller.signal)])
        applyChat(header)
        pendingScroll.current = { kind: 'bottom' }
        setMessages(page.items)
        setHasMore(page.has_more)
        lastId.current = page.items.at(-1)?.id ?? 0
        setStatus('ready')
        setError(null)
      } catch (cause) {
        const apiError = toApiError(cause)
        if (apiError.isCancelled) return
        setError(apiError)
        setStatus('error')
      }
    }
    void run()
    return () => controller.abort()
  }, [chatId, applyChat, reloadKey])

  // New messages every 3 s while the page is visible.
  useEffect(() => {
    if (status !== 'ready' || !visible) return
    let busy = false
    const timer = window.setInterval(async () => {
      if (busy) return
      busy = true
      try {
        const page = await chatService.messages(chatId, { after_id: lastId.current, limit: 100 })
        if (page.items.length) {
          if (atBottom.current) pendingScroll.current = { kind: 'bottom' }
          setMessages((current) => merge(current, page.items))
          lastId.current = Math.max(lastId.current, page.items.at(-1)!.id)
        }
      } catch {
        // The next tick retries.
      } finally {
        busy = false
      }
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [chatId, status, visible])

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  // Read up to the last message once it is on a visible screen.
  const lastMessageId = messages.at(-1)?.id ?? 0
  const member = chat?.is_member ?? false
  useEffect(() => {
    if (!visible || !member || lastMessageId <= readSent.current) return
    readSent.current = lastMessageId
    chatService
      .read(chatId, lastMessageId)
      .then(() => chatStore.changed())
      .catch(() => {
        readSent.current = 0
      })
  }, [chatId, lastMessageId, member, visible, chatStore])

  useLayoutEffect(() => {
    const node = scroller.current
    const pending = pendingScroll.current
    if (!node || !pending) return
    pendingScroll.current = null
    if (pending.kind === 'bottom') node.scrollTop = node.scrollHeight
    else node.scrollTop = node.scrollHeight - pending.height + pending.top
  }, [messages, status])

  // The composer grows with its text up to ~7 lines.
  useLayoutEffect(() => {
    const node = input.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 168)}px`
  }, [draft])

  // Object URLs of the pending image previews are released with them.
  const previews = useRef(new Set<string>())
  useEffect(() => {
    const urls = previews.current
    return () => urls.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  const loadOlder = async () => {
    const first = messages[0]
    if (!first || older === 'loading') return
    setOlder('loading')
    try {
      const page = await chatService.messages(chatId, { before_id: first.id, limit: PAGE })
      const node = scroller.current
      if (node) pendingScroll.current = { kind: 'keep', height: node.scrollHeight, top: node.scrollTop }
      setMessages((current) => merge(current, page.items))
      setHasMore(page.has_more)
      setOlder('idle')
    } catch {
      setOlder('error')
    }
  }

  const onScroll = () => {
    const node = scroller.current
    if (!node) return
    atBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < NEAR_BOTTOM
    if (node.scrollTop < 120 && hasMore && older === 'idle') void loadOlder()
  }

  const addFiles = (list: FileList | File[]) => {
    let room = MAX_FILES - files.length
    for (const file of Array.from(list)) {
      if (room <= 0) {
        toasts.error(t('chat.composer.tooMany', { max: MAX_FILES }))
        break
      }
      if (file.size > MAX_FILE_SIZE) {
        toasts.error(t('chat.composer.tooLarge', { name: file.name }))
        continue
      }
      room -= 1
      const id = newClientMessageId()
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
      if (preview) previews.current.add(preview)
      setFiles((current) => [...current, { id, name: file.name, size: file.size, content_type: file.type || 'application/octet-stream', progress: 0, preview }])
      filesService
        .upload(file, (progress) => setFiles((current) => current.map((row) => (row.id === id ? { ...row, progress } : row))))
        .then((uploaded) => setFiles((current) => current.map((row) => (row.id === id ? { ...row, uploaded, progress: 100 } : row))))
        .catch((cause) => {
          setFiles((current) => current.map((row) => (row.id === id ? { ...row, failed: true } : row)))
          toasts.error(t('chat.composer.uploadFailed', { name: file.name }), toApiError(cause).message)
        })
    }
  }

  const removeFile = (id: string) => setFiles((current) => current.filter((row) => row.id !== id))

  const uploading = files.some((file) => !file.uploaded && !file.failed)
  const ready = files.filter((file) => file.uploaded)
  const body = draft.trim()
  const tooLong = draft.length > MAX_BODY
  const canSend = !sending && !uploading && !tooLong && (editing ? body.length > 0 : body.length > 0 || ready.length > 0)

  const startEdit = (message: ChatMessage) => {
    if (!editing) savedDraft.current = draft
    setEditing(message)
    setDraft(message.body)
    window.requestAnimationFrame(() => input.current?.focus())
  }

  const stopEdit = () => {
    setEditing(null)
    setDraft(savedDraft.current)
    savedDraft.current = ''
  }

  const submit = async () => {
    if (!canSend) return
    setSending(true)
    try {
      if (editing) {
        const updated = await chatService.edit(chatId, editing.id, body)
        setMessages((current) => merge(current, [updated]))
        stopEdit()
      } else {
        const sent = await chatService.send(chatId, {
          body,
          attachments: ready.map(({ uploaded }) => ({ key: uploaded!.key, name: uploaded!.name, size: uploaded!.size, content_type: uploaded!.content_type })),
          client_msg_id: clientId.current,
        })
        clientId.current = newClientMessageId()
        pendingScroll.current = { kind: 'bottom' }
        setMessages((current) => merge(current, [sent]))
        lastId.current = Math.max(lastId.current, sent.id)
        // Sending marks the chat read on the server.
        readSent.current = Math.max(readSent.current, sent.id)
        setDraft('')
        setFiles((current) => current.filter((file) => !file.uploaded))
        if (!chat?.is_member) void reloadChat()
        chatStore.changed()
      }
    } catch (cause) {
      toasts.error(editing ? t('chat.editFailed') : t('chat.sendFailed'), toApiError(cause).message)
    } finally {
      setSending(false)
      input.current?.focus()
    }
  }

  const toggleMute = async () => {
    if (!chat) return
    try {
      await chatService.mute(chatId, !chat.muted)
      applyChat({ ...chat, muted: !chat.muted })
      chatStore.changed()
    } catch (cause) {
      toasts.error(t('chat.muteFailed'), toApiError(cause).message)
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      void submit()
    } else if (event.key === 'Escape' && editing) {
      event.preventDefault()
      event.stopPropagation()
      stopEdit()
    }
  }

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (editing || event.clipboardData.files.length === 0) return
    event.preventDefault()
    addFiles(event.clipboardData.files)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (editing || !event.dataTransfer.files.length) return
    event.preventDefault()
    addFiles(event.dataTransfer.files)
  }

  const subtitle = chat
    ? chat.kind === 'order'
      ? [chat.order_status && statusLabel(chat.order_status, t), t('chat.members', { count: chat.member_count })].filter(Boolean).join(' · ')
      : chat.kind === 'group'
        ? t('chat.members', { count: chat.member_count })
        : roleLabel(chat.peer_role_code, t)
    : ''

  return (
    <div className="flex min-h-0 flex-1 flex-col" onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
      <header className="flex shrink-0 items-center gap-2.5 border-b border-line px-3 py-2.5">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} aria-label={t('chat.back')} title={t('chat.back')}>
            <ArrowLeft className="size-4" />
          </Button>
        )}
        {chat && <ChatAvatar chat={chat} size={34} />}
        <div className="min-w-0 flex-1">
          <p className={cn('truncate font-semibold text-fg', textSize(14.5))}>{chat ? chatTitle(chat, t) : '…'}</p>
          {subtitle && <p className={cn('truncate text-muted', textSize(12))}>{subtitle}</p>}
        </div>
        {chat && orderLink && chat.kind === 'order' && chat.order_id && session.can('sales_orders.read') && (
          <Button
            variant="ghost"
            size="icon"
            title={chatTitle(chat, t)}
            aria-label={chatTitle(chat, t)}
            onClick={() => {
              chatStore.closeDrawer()
              navigate(`/sales/orders?sheet=${encodeURIComponent(chat.order_id)}`)
            }}
          >
            <ExternalLink className="size-4" />
          </Button>
        )}
        {chat?.is_member && (
          <Button variant="ghost" size="icon" onClick={() => void toggleMute()} aria-label={chat.muted ? t('chat.unmute') : t('chat.mute')} title={chat.muted ? t('chat.unmute') : t('chat.mute')}>
            {chat.muted ? <BellOff className="size-4" /> : <Bell className="size-4" />}
          </Button>
        )}
        {chat && headerActions?.(chat)}
      </header>

      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto py-2">
        {status === 'loading' && (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        )}
        {status === 'error' && error && (
          <div className="p-4">
            <ErrorState
              compact
              error={error}
              onRetry={() => {
                setStatus('loading')
                setReloadKey((key) => key + 1)
              }}
            />
          </div>
        )}
        {status === 'ready' && chat && (
          <>
            {hasMore && (
              <div className="flex justify-center py-1.5">
                {older === 'loading' ? (
                  <Spinner />
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => void loadOlder()}>
                    {older === 'error' ? t('chat.olderFailed') : t('chat.loadOlder')}
                  </Button>
                )}
              </div>
            )}
            {messages.length === 0 && <p className={cn('px-4 py-10 text-center text-muted', textSize(13))}>{t('chat.noMessages')}</p>}
            {messages.map((message, index) => {
              const previous = messages[index - 1]
              const newDay = !previous || dayKey(previous.created_at) !== dayKey(message.created_at)
              const showName =
                newDay ||
                !previous ||
                previous.kind === 'system' ||
                previous.sender_profile_id !== message.sender_profile_id ||
                new Date(message.created_at).getTime() - new Date(previous.created_at).getTime() > RUN_MS
              return (
                <Fragment key={message.id}>
                  {newDay && (
                    <div className="flex justify-center py-2">
                      <span className={cn('num rounded-full bg-hover px-2.5 py-0.5 font-medium text-muted', textSize(11.5))}>{formatDayLabel(message.created_at, t)}</span>
                    </div>
                  )}
                  <MessageRow message={message} chat={chat} showName={showName} onEdit={startEdit} />
                </Fragment>
              )
            })}
          </>
        )}
      </div>

      {status === 'ready' && chat && (
        <footer className="shrink-0 border-t border-line px-3 pb-3 pt-2">
          {!chat.is_member && <p className={cn('mb-2 rounded-[calc(8px*var(--radius-scale))] bg-info-soft px-2.5 py-1.5 text-info', textSize(12))}>{t('chat.notMember')}</p>}
          {editing && (
            <div className={cn('mb-2 flex items-center gap-2 border-l-2 border-accent pl-2', textSize(12))}>
              <Pencil className="size-3.5 shrink-0 text-accent" />
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-accent">{t('chat.composer.editing')}</span>
                <span className="block truncate text-muted">{editing.body}</span>
              </span>
              <Button variant="ghost" size="icon-sm" onClick={stopEdit} aria-label={t('chat.composer.cancelEdit')} title={t('chat.composer.cancelEdit')}>
                <X className="size-4" />
              </Button>
            </div>
          )}
          {files.length > 0 && !editing && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {files.map((file) => (
                <span
                  key={file.id}
                  className={cn(
                    'flex max-w-[220px] items-center gap-1.5 rounded-[calc(8px*var(--radius-scale))] border py-1 pl-1 pr-1.5',
                    textSize(12),
                    file.failed ? 'border-danger text-danger' : 'border-line text-fg-2',
                  )}
                >
                  {file.preview ? (
                    <img src={file.preview} alt="" className="size-7 shrink-0 rounded-[calc(5px*var(--radius-scale))] object-cover" />
                  ) : (
                    <FileText className="ml-1 size-4 shrink-0 text-muted" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{file.name}</span>
                    <span className={cn('num block text-muted', textSize(10.5))}>
                      {file.failed ? t('chat.composer.notUploaded') : file.uploaded ? formatSize(file.size) : `${t('chat.composer.uploading')} ${file.progress}%`}
                    </span>
                  </span>
                  <button type="button" onClick={() => removeFile(file.id)} aria-label={t('chat.composer.remove')} title={t('chat.composer.remove')} className="rounded p-0.5 text-muted hover:bg-hover hover:text-fg">
                    <X className="size-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-1.5">
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                if (event.target.files) addFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              disabled={Boolean(editing)}
              onClick={() => fileInput.current?.click()}
              aria-label={t('chat.composer.attach')}
              title={t('chat.composer.attach')}
            >
              <Paperclip className="size-4" />
            </Button>
            <textarea
              ref={input}
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              placeholder={t('chat.composer.placeholder')}
              aria-label={t('chat.composer.placeholder')}
              title={t('chat.composer.hint')}
              className={cn(
                'max-h-[168px] min-h-[calc(var(--spacing)*8.5*var(--ui-size))] flex-1 resize-none rounded-[var(--radius-control)] border border-field-border bg-field px-3 py-[7px] text-fg outline-none transition-colors placeholder:text-subtle focus:border-accent',
                textSize(13.5),
                tooLong && 'border-danger focus:border-danger',
              )}
            />
            <Button
              variant="primary"
              size="icon"
              disabled={!canSend}
              loading={sending}
              onClick={() => void submit()}
              aria-label={editing ? t('chat.composer.save') : t('chat.composer.send')}
              title={editing ? t('chat.composer.save') : t('chat.composer.send')}
            >
              {!sending && <SendHorizontal className="size-4" />}
            </Button>
          </div>
          {tooLong && <p className={cn('mt-1 text-danger', textSize(12))}>{t('chat.composer.tooLong', { max: MAX_BODY })}</p>}
        </footer>
      )}
    </div>
  )
})
